const REGIONS = ['APJ', 'EMEA', 'NA', 'LATAM'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toTrendData = (rows, valueKey) => {
  const dataRows = safeArray(rows);
  return {
    xData: dataRows.map((item) => item?.period || item?.label || ''),
    data: dataRows.map((item) => toNumber(item?.[valueKey])),
  };
};

const mapRegionalOverview = (regionalOverview) => {
  const source = regionalOverview && typeof regionalOverview === 'object' ? regionalOverview : {};

  return REGIONS.reduce((acc, region) => {
    const regionData = source[region] || source[region.toLowerCase()] || {};
    acc[region] = {
      topNarratives: safeArray(regionData?.topNarratives).map((item) =>
        typeof item === 'string' ? item : item?.narrative || item?.Narrative || ''
      ).filter(Boolean).slice(0, 3),
      avgRunsPerPartnerUser: toNumber(regionData?.avgRunsPerPartnerUser),
    };
    return acc;
  }, {});
};

const mapTopNarratives = (topNarrativesUsedByPartners) =>
  safeArray(topNarrativesUsedByPartners).map((item) => ({
    name: item?.narrative || item?.Narrative || 'Unknown',
    count: toNumber(item?.runs ?? item?.Count ?? item?.value),
  }));

const mapTrendCompanies = (companiesObject, valueKey) => {
  const source = companiesObject && typeof companiesObject === 'object' ? companiesObject : {};
  return Object.entries(source).reduce((acc, [company, rows]) => {
    acc[company] = toTrendData(rows, valueKey);
    return acc;
  }, {});
};

const mapCompanyNarrativeBreakdown = (rows) => {
  const companies = safeArray(rows);
  const xData = companies.map((item) => item?.company || item?.name || 'Unknown');
  const narrativeSet = new Set();

  companies.forEach((item) => {
    safeArray(item?.narratives).forEach((narrativeRow) => {
      const narrativeName = narrativeRow?.narrative || narrativeRow?.Narrative;
      if (narrativeName) narrativeSet.add(narrativeName);
    });
  });

  const narratives = Array.from(narrativeSet);
  const series = narratives.map((narrative) => ({
    name: narrative,
    data: companies.map((company) => {
      const row = safeArray(company?.narratives).find(
        (item) => (item?.narrative || item?.Narrative) === narrative
      );
      return toNumber(row?.runs ?? row?.Count ?? row?.value);
    }),
  }));

  return { xData, series };
};

const mapRegionalNarrativeBreakdown = (regionalBreakdown) => {
  const source = regionalBreakdown && typeof regionalBreakdown === 'object' ? regionalBreakdown : {};
  return REGIONS.reduce((acc, region) => {
    const rows = safeArray(source[region] || source[region.toLowerCase()]);
    const xData = rows.map((item) => item?.narrative || item?.Narrative || 'Unknown');
    const values = rows.map((item) => toNumber(item?.runs ?? item?.Count ?? item?.value));
    acc[region] = {
      xData,
      series: [
        {
          name: region,
          data: values,
        },
      ],
    };
    return acc;
  }, {});
};

const INGESTION_CATEGORIES = ['Instant Run', 'One-time Schedule', 'Recurring Schedule', 'Unknown'];

const mapRegionalIngestion = (regionalIngestion) => {
  const source = regionalIngestion && typeof regionalIngestion === 'object' ? regionalIngestion : {};

  return REGIONS.reduce((acc, region) => {
    const regionData = source[region] || source[region.toLowerCase()] || {};
    const typesRaw = Array.isArray(regionData) ? regionData : safeArray(regionData?.types);
    const normalizedTypes = INGESTION_CATEGORIES.map((category) => {
      const match = typesRaw.find((item) => (item?.name || item?.type) === category);
      const value = toNumber(match?.value ?? match?.runs ?? match?.count);
      const percent = toNumber(match?.percent ?? match?.percentage);
      return {
        name: category,
        value,
        percent: percent || value,
      };
    });

    const total = toNumber(regionData?.total)
      || normalizedTypes.reduce((sum, item) => sum + toNumber(item.value), 0);

    acc[region] = {
      total,
      types: normalizedTypes,
    };
    return acc;
  }, {});
};

const buildCompanyOptions = (partnerCompanies) => {
  const options = new Set();
  safeArray(partnerCompanies).forEach((company) => {
    if (company?.company) options.add(company.company);
  });
  return Array.from(options);
};

const mapCompanyIngestionOverview = (ingestionOverview) => {
  if (!ingestionOverview) {
    return { total: 0, types: [] };
  }

  if (Array.isArray(ingestionOverview.types)) {
    const total = toNumber(ingestionOverview.total)
      || ingestionOverview.types.reduce(
        (sum, item) => sum + toNumber(item?.value ?? item?.runs ?? item?.count),
        0
      );
    return {
      total,
      types: ingestionOverview.types.map((item) => ({
        name: item?.name || item?.type || 'Unknown',
        value: toNumber(item?.value ?? item?.runs ?? item?.count),
        percent: toNumber(item?.percent ?? item?.percentage),
      })),
    };
  }

  const rows = Array.isArray(ingestionOverview) ? ingestionOverview : Object.entries(ingestionOverview).map(([name, value]) => ({ name, value }));
  const total = rows.reduce((sum, item) => sum + toNumber(item?.value ?? item?.runs ?? item?.count), 0);
  return {
    total,
    types: rows.map((item) => ({
      name: item?.name || item?.type || 'Unknown',
      value: toNumber(item?.value ?? item?.runs ?? item?.count),
      percent: toNumber(item?.percent ?? item?.percentage),
    })),
  };
};

const mapCompanyUserNarrativeBreakdown = (rows) =>
  safeArray(rows).map((item) => ({
    name: item?.name || item?.user || item?.email || 'Unknown',
    ...(safeArray(item?.narratives).reduce((acc, narrativeRow) => {
      const narrativeName = narrativeRow?.narrative || narrativeRow?.Narrative || 'Unknown';
      acc[narrativeName] = toNumber(narrativeRow?.runs ?? narrativeRow?.Count ?? narrativeRow?.value);
      return acc;
    }, {})),
  }));

const mapCompanyUserBookingBreakdown = (rows) =>
  safeArray(rows).map((item) => ({
    name: item?.name || item?.user || item?.email || 'Unknown',
    value: toNumber(item?.bookings ?? item?.runs ?? item?.value ?? item?.count),
  }));

const mapActivePartnerUsers = (activePartnerUsers) =>
  safeArray(activePartnerUsers?.users).map((item) => ({
    userId: item?.userId || '',
    name: item?.name || 'Unknown',
    email: item?.email || null,
    company: item?.company || '-',
    region: item?.region || '-',
    totalRuns: toNumber(item?.totalRuns),
    totalBookings: toNumber(item?.totalBookings),
    isActive: item?.isActive === true,
  }));

export const createEmptyPartnerAnalytics = () => ({
  kpis: {
    partnerCompanies: 0,
    activePartnerUsers: 0,
    globalAvgRunsPerPartnerUser: 0,
    totalPartnerNarrativeRuns: 0,
    totalPartnerBookings: 0,
  },
  activePartnerUsers: {
    total: 0,
    users: [],
  },
  partnerCompanies: [],
  regionalOverview: mapRegionalOverview({}),
  topNarratives: [],
  narrativeUsageTrendTotal: { xData: [], data: [] },
  narrativeUsageTrendCompanies: {},
  companyBreakdown: { xData: [], series: [] },
  regionalBreakdownByRegion: mapRegionalNarrativeBreakdown({}),
  ingestionByRegion: mapRegionalIngestion({}),
  bookingTrendTotal: { xData: [], data: [] },
  bookingTrendCompanies: {},
  companyOptions: [],
});

export const createEmptyPartnerCompanyAnalytics = () => ({
  kpis: {
    activePartnerUsers: 0,
    totalNarrativeRuns: 0,
    avgNarrativeRunsPerPartnerUser: 0,
    totalBookings: 0,
  },
  topNarratives: [],
  ingestion: { total: 0, types: [] },
  narrativeUsageTrend: { xData: [], data: [] },
  bookingTrend: { xData: [], data: [] },
  userNarrativeRunBreakdown: [],
  userBookingBreakdown: [],
  userDetails: [],
});

export const mapPartnerAnalyticsResponse = (partnerAnalytics = {}) => {
  const narrativeUsageTrendCompanies = mapTrendCompanies(
    partnerAnalytics?.partnerNarrativeUsageTrend?.companies,
    'runs'
  );
  const bookingTrendCompanies = mapTrendCompanies(
    partnerAnalytics?.partnerBookingVolumeTrend?.companies,
    'bookings'
  );
  const activeUsers = mapActivePartnerUsers(partnerAnalytics?.activePartnerUsers);
  const activeUserTotal =
    toNumber(partnerAnalytics?.activePartnerUsers?.total) || activeUsers.length || 0;

  return {
    kpis: {
      partnerCompanies: safeArray(partnerAnalytics?.partnerCompanies).length,
      activePartnerUsers: activeUserTotal,
      globalAvgRunsPerPartnerUser: toNumber(
        partnerAnalytics?.globalAvgNarrativeRunsPerPartnerUser?.value
      ),
      totalPartnerNarrativeRuns: toNumber(partnerAnalytics?.totalPartnerNarrativeRuns?.total),
      totalPartnerBookings: toNumber(partnerAnalytics?.totalPartnerBookings?.total),
    },
    activePartnerUsers: {
      total: activeUserTotal,
      users: activeUsers,
    },
    partnerCompanies: safeArray(partnerAnalytics?.partnerCompanies).map((item) => ({
      name: item?.company || 'Unknown',
      region: item?.region || '-',
      totalUsers: toNumber(item?.totalUsers),
      activeUsers: toNumber(item?.activeUsers),
      totalRuns: toNumber(item?.totalRuns),
      totalBookings: toNumber(item?.totalBookings),
    })),
    regionalOverview: mapRegionalOverview(partnerAnalytics?.partnerRegionalOverview),
    topNarratives: mapTopNarratives(partnerAnalytics?.topNarrativesUsedByPartners),
    narrativeUsageTrendTotal: toTrendData(
      partnerAnalytics?.partnerNarrativeUsageTrend?.total,
      'runs'
    ),
    narrativeUsageTrendCompanies,
    companyBreakdown: mapCompanyNarrativeBreakdown(
      partnerAnalytics?.partnerCompanyNarrativeBreakdown
    ),
    regionalBreakdownByRegion: mapRegionalNarrativeBreakdown(
      partnerAnalytics?.partnerRegionalNarrativeBreakdown
    ),
    ingestionByRegion: mapRegionalIngestion(partnerAnalytics?.partnerRegionalIngestionTypeOverview),
    bookingTrendTotal: toTrendData(partnerAnalytics?.partnerBookingVolumeTrend?.total, 'bookings'),
    bookingTrendCompanies,
    companyOptions: buildCompanyOptions(partnerAnalytics?.partnerCompanies),
  };
};

export const mapPartnerCompanyAnalyticsResponse = (partnerCompanyAnalytics = {}) => ({
  kpis: {
    activePartnerUsers: toNumber(partnerCompanyAnalytics?.activePartnerUsers?.total),
    totalNarrativeRuns: toNumber(partnerCompanyAnalytics?.totalNarrativeRuns?.total),
    avgNarrativeRunsPerPartnerUser: toNumber(
      partnerCompanyAnalytics?.avgNarrativeRunsPerPartnerUser?.value
    ),
    totalBookings: toNumber(partnerCompanyAnalytics?.totalBookings?.total),
  },
  topNarratives: mapTopNarratives(partnerCompanyAnalytics?.topNarrativesUsedByCompany),
  ingestion: mapCompanyIngestionOverview(partnerCompanyAnalytics?.companyIngestionTypeOverview),
  narrativeUsageTrend: toTrendData(partnerCompanyAnalytics?.companyNarrativeUsageTrend, 'runs'),
  bookingTrend: toTrendData(partnerCompanyAnalytics?.companyBookingVolumeTrend, 'bookings'),
  userNarrativeRunBreakdown: mapCompanyUserNarrativeBreakdown(
    partnerCompanyAnalytics?.companyUserNarrativeRunBreakdown
  ),
  userBookingBreakdown: mapCompanyUserBookingBreakdown(
    partnerCompanyAnalytics?.companyUserBookingBreakdown
  ),
  userDetails: safeArray(partnerCompanyAnalytics?.userDetails),
});

export { REGIONS };
